// AGC: Category selector — grid of category buttons with icons + custom category inline-add
import React, { useRef, useState } from 'react'
import { Check, Plus, X } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import type { VoiceFieldProps } from './types'

// AGC: Category icons for task, revenue, and expense categories
const CAT_ICONS: Record<string, string> = {
  irrigacao: '💧', pulverizacao: '🧴', adubacao: '🧪', plantio: '🌱',
  colheita: '🚜', manutencao: '🔧', monitoramento: '👁️', preparo_solo: '🏗️',
  dessecacao: '🌿', outros: '📋', other: '📋',
  grain_sale: '🌾', livestock_sale: '🐄', machinery_sale: '🚜',
  land_sale: '🏞️', product_sale: '📦', agrochemical_sale: '🧪',
  tool_sale: '🔨', stock_sale: '📦', animal_sale: '🐄',
  service: '🔧', rental: '🏠', subsidy: '💰',
  fuel: '⛽', inputs: '🧪', equipment: '🔨', machinery_parts: '🔩',
  machinery_purchase: '🚜', land_purchase: '🏞️',
  maintenance: '🔧', labor: '👷', transport: '🚛', rent: '🏠',
}

// AGC: Extra props for category field beyond base VoiceFieldProps
interface CategoryFieldProps extends VoiceFieldProps {
  /** AGC: User-created custom financial categories */
  customFinancialCategories: Array<{ id: string; label: string; stockTreatment?: string }>
  /** AGC: Callback to persist a new custom category */
  onAddCustomCategory: (label: string) => void
}

export const CategoryField: React.FC<CategoryFieldProps> = ({
  info,
  inputValues,
  setInputValues,
  lastResponse,
  wizardMode,
  customFinancialCategories,
  onAddCustomCategory,
}) => {
  const { t } = useTranslation()
  // AGC: Inline category add state
  const [isAddingCategory, setIsAddingCategory] = useState(false)
  const [newCategoryLabel, setNewCategoryLabel] = useState('')
  const categoryInputRef = useRef<HTMLInputElement>(null)

  const handleAddCustomCategory = () => {
    const label = newCategoryLabel.trim()
    if (!label) return
    onAddCustomCategory(label)
    setIsAddingCategory(false)
    setNewCategoryLabel('')
  }

  // AGC: Merge backend suggestions with user-created custom categories
  const backendSuggestions = info.suggestions || []
  const customSuggestions = customFinancialCategories
    .filter(c => !backendSuggestions.some((s: { value: string }) => s.value === c.id))
    .map(c => ({ value: c.id, label: c.label, confidence: 70 }))
  const allSuggestions = [...backendSuggestions, ...customSuggestions]
  const selectedCat = inputValues.category || lastResponse?.parsedData?.category

  // AGC: Sizing classes — bigger in wizard mode to fill the full-screen layout
  const sz = wizardMode
    ? { card: 'p-5', label: 'text-base', reason: 'text-sm', btn: 'gap-2 px-3 py-3.5 rounded-lg text-base', icon: 'text-xl', addBtn: 'gap-2 px-3 py-3.5 rounded-lg text-base', input: 'px-3 py-3 text-base', checkIcon: 'w-5 h-5', grid: 'gap-2.5' }
    : { card: 'p-2', label: 'text-xs', reason: 'text-xs', btn: 'gap-1 px-2 py-1.5 rounded text-xs', icon: '', addBtn: 'gap-1 px-2 py-1.5 rounded text-xs', input: 'px-2 py-1.5 text-xs', checkIcon: 'w-3 h-3', grid: 'gap-1' }

  return (
    <div className={`${sz.card} bg-white/5 border border-white/20 rounded-lg`}>
      <div className="mb-2">
        <span className={`text-white/80 ${sz.label} font-medium flex items-center gap-1`}>
          📋 {info.fieldLabel}
        </span>
        {info.reason && (
          <p className={`text-white/40 ${sz.reason} mt-0.5`}>{info.reason}</p>
        )}
      </div>
      <div className={`grid grid-cols-2 ${sz.grid}`}>
        {allSuggestions.map((s: { value: string; label: string; confidence: number }) => (
          <button
            key={s.value}
            onClick={() => setInputValues(prev => ({ ...prev, category: s.value }))}
            className={`flex items-center ${sz.btn} text-left transition-colors ${
              selectedCat === s.value
                ? 'bg-emerald-500/30 border border-emerald-500/50 text-emerald-300 font-medium'
                : s.confidence > 90
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-200 hover:bg-emerald-500/20'
                  : 'bg-white/5 hover:bg-white/10 text-white/80 border border-transparent'
            }`}
          >
            <span className={sz.icon}>{CAT_ICONS[s.value] || '🏷️'}</span>
            <span className="truncate">{s.label}</span>
          </button>
        ))}

        {/* AGC: "+" button to add custom category — same as Sistema > Categorias flow */}
        {!isAddingCategory ? (
          <button
            onClick={() => {
              setIsAddingCategory(true)
              setTimeout(() => categoryInputRef.current?.focus(), 50)
            }}
            className={`flex items-center ${sz.addBtn} text-left transition-colors bg-white/5 hover:bg-white/10 text-white/50 border border-dashed border-white/20 hover:border-white/40`}
          >
            <Plus className={sz.checkIcon} />
            <span className="truncate">{t('voice.addCategory')}</span>
          </button>
        ) : (
          <div className="col-span-2 flex items-center gap-1">
            <input
              ref={categoryInputRef}
              type="text"
              placeholder={t('voice.categoryName')}
              value={newCategoryLabel}
              onChange={(e) => setNewCategoryLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddCustomCategory()
                if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryLabel('') }
              }}
              className={`flex-1 ${sz.input} bg-white/10 border border-white/20 rounded text-white placeholder-white/30 focus:outline-none focus:border-emerald-500`}
            />
            <button
              onClick={handleAddCustomCategory}
              disabled={!newCategoryLabel.trim()}
              className={`${sz.input} bg-emerald-500/30 border border-emerald-500/50 rounded text-emerald-300 disabled:opacity-30`}
            >
              <Check className={sz.checkIcon} />
            </button>
            <button
              onClick={() => { setIsAddingCategory(false); setNewCategoryLabel('') }}
              className={`px-1.5 py-1.5 bg-white/5 rounded text-white/50`}
            >
              <X className={sz.checkIcon} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
